import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../../services/notificationService';
import { formatRelativeTime } from '../../lib/utils';
import { ROUTES } from '../../config/routes';
import { useAuthStore } from '../../stores/authStore';
import { ProLockOverlay } from '../common';
import type { Notification } from '../../types';

interface NotificationMenuProps {
  open: boolean;
  onClose: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
    case 'sync_complete':
    case 'purchase_complete':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'error':
    case 'sync_error':
    case 'payment_failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'warning':
    case 'low_balance':
    case 'expiry_warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
};

export const NotificationMenu: React.FC<NotificationMenuProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [proLockOpen, setProLockOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', { limit: 5 }],
    queryFn: () => notificationService.getNotifications({ limit: 5 }),
    enabled: open,
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationService.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      if (['trial', 'starter'].includes(user?.plan || '')) {
        setProLockOpen(true);
        return;
      }
      navigate(notification.link);
    }
    onClose();
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const notifications = data?.data?.notifications || [];
  const unreadCount = data?.data?.unread_count || 0;

  if (!open && !proLockOpen) return null;

  return (
    <>
    {proLockOpen && (
      <ProLockOverlay
        variant="modal"
        title={<>この機能は<span style={{ color: '#0a7a72' }}>Proプラン</span>から</>}
        description="Proプランにアップグレードすると、リード一覧の閲覧など全機能がご利用いただけます。"
        onUpgrade={() => { setProLockOpen(false); navigate(ROUTES.SUBSCRIPTION); onClose(); }}
      />
    )}
    {open && <div className="absolute right-0 top-full mt-1 w-[320px] max-h-[480px] overflow-y-auto bg-white rounded-lg shadow-lg border z-50">
      {/* ヘッダー */}
      <div className="px-4 py-3 flex items-center justify-between border-b">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">通知</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-primary text-white rounded">{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead} disabled={markAllAsReadMutation.isPending}>すべて既読</Button>
        )}
      </div>

      {/* 通知リスト */}
      {isLoading ? (
        <div className="py-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : notifications.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">通知はありません</p>
      ) : (
        <>
          {notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 border-b last:border-b-0 ${!notification.is_read ? 'bg-blue-50/30' : ''}`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {getNotificationIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${notification.is_read ? '' : 'font-semibold'}`}>{notification.title}</p>
                <p className="text-xs text-gray-500 line-clamp-2">{notification.message}</p>
                <p className="text-xs text-gray-400 mt-0.5">{formatRelativeTime(notification.created_at)}</p>
              </div>
            </button>
          ))}
        </>
      )}

      {/* フッター: すべて見る */}
      <div className="px-4 py-2 border-t text-center">
        <button
          className="text-xs text-primary hover:underline"
          onClick={() => { navigate(ROUTES.NOTIFICATIONS); onClose(); }}
        >
          すべて見る
        </button>
      </div>
    </div>}
    </>
  );
};
